<template>
  <v-container fluid>
    <v-card v-if="template" variant="outlined">
      <v-card-title class="d-flex flex-wrap align-center ga-2 bg-blue-grey-lighten-4">
        <span>Production Template {{ template.code }}</span>
        <v-spacer />
        <v-chip color="primary" variant="tonal" size="small">Version {{ template.version_no || 1 }}</v-chip>
        <v-chip :color="template.status == 1 ? 'success' : 'grey'" size="small">{{ template.status == 1 ? 'Active' : 'Inactive' }}</v-chip>
        <v-btn class="d-print-none" icon="mdi-printer" variant="text" @click="printPage" />
      </v-card-title>
      <v-card-text class="pt-4">
        <v-row dense>
          <v-col cols="6" md="3"><Info label="Template Name" :value="template.name" /></v-col>
          <v-col cols="6" md="3"><Info label="Finished Product" :value="`${template.product?.code || ''} - ${template.product?.name || ''}`" /></v-col>
          <v-col cols="6" md="3"><Info label="Default Batch" :value="`${number(template.default_batch_quantity || 25)} ${template.product?.unit?.name || 'kg'}`" /></v-col>
          <v-col cols="6" md="3"><Info label="Effective From" :value="dateOnly(template.effective_from) || '-'" /></v-col>
        </v-row>

        <v-alert type="info" variant="tonal" density="compact" class="my-4">
          Formula quantities are defined for the default batch. Planned output scales every row; Standard Purity is taken from the formula override when configured, otherwise from Product Master. Batch Balancer uses the remaining finished batch weight.
        </v-alert>

        <v-table density="compact" class="border-sm">
          <thead>
            <tr>
              <th>#</th>
              <th>Raw / Packaging Product</th>
              <th>Type</th>
              <th>Calculation</th>
              <th class="text-center">Standard Purity</th>
              <th class="text-end">Formula Qty</th>
              <th class="text-end">Loss %</th>
              <th class="text-end">Qty + Loss</th>
              <th class="text-end">Sequence</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in template.items || []" :key="item.id">
              <td>{{ index + 1 }}</td>
              <td>{{ item.raw_product?.code }} - {{ item.raw_product?.name }}</td>
              <td>{{ labelize(item.item_type || 'raw_material') }}</td>
              <td>{{ labelize(item.calculation_method || 'fixed') }}</td>
              <td class="text-center">{{ item.item_type === 'packaging' || item.calculation_method === 'balancer' ? '-' : `${number(item.standard_purity ?? item.raw_product?.standard_purity ?? 100, 4)}%` }}</td>
              <td class="text-end">{{ number(item.quantity) }}</td>
              <td class="text-end">{{ number(item.process_loss_percent, 4) }}%</td>
              <td class="text-end font-weight-bold">{{ number(Number(item.quantity || 0) * (1 + Number(item.process_loss_percent || 0) / 100), 6) }}</td>
              <td class="text-end">{{ item.sequence ?? index + 1 }}</td>
            </tr>
          </tbody>
        </v-table>
      </v-card-text>
    </v-card>
    <v-skeleton-loader v-else type="article, table" />
  </v-container>
</template>

<script setup>
import { defineComponent, h, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";

const Info = defineComponent({
  props: { label: String, value: [String, Number] },
  setup(props) {
    return () => h("div", { class: "info-box" }, [
      h("div", { class: "info-label" }, props.label),
      h("div", { class: "font-weight-medium" }, props.value ?? "-"),
    ]);
  },
});

const route = useRoute();
const store = useStore();
const template = ref(null);

function number(value, digits = 4) {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: digits });
}

function dateOnly(value) {
  return value ? String(value).slice(0, 10) : "";
}

function labelize(value) {
  return String(value || "-").replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function printPage() {
  window.print();
}

onMounted(async () => {
  try {
    const response = await store.dispatch("getTemplateById", route.params.id);
    template.value = response?.data || response;
  } catch (error) {
    toast.error(error?.response?.data?.message || "Production template could not be loaded.");
  }
});
</script>

<style scoped>
.info-box { border: 1px solid rgba(0, 0, 0, 0.12); border-radius: 4px; height: 100%; padding: 8px 10px; }
.info-label { color: rgba(0, 0, 0, 0.6); font-size: 0.7rem; text-transform: uppercase; }
</style>
